/**
 * Soft-deferred registration schedule swaps wait for a frame/loop boundary so the
 * on-air creative is not cut mid-slot. On Android, same-frame video `ended`→restart
 * and pending-surface rematerialize thrash can starve that boundary for minutes (#1577).
 * Cap how long we wait before forcing apply — long enough to finish a typical clip
 * (Publicar has 33s slots), short enough to bound a stuck timer.
 *
 * Once head assets are ready (`ready_at_boundary`), cap remaining wait so a long
 * still/dwell does not make Studio assign feel stuck; hybrid `onEnded` / frame
 * boundary still flush earlier when they fire first.
 */

/**
 * Absolute starvation ceiling before forcing a deferred registration schedule apply.
 * #1577 field lag was 80–200s+ without a ceiling; 8s cut mid-clip on 20–33s videos
 * while still downloading — keep this high until head is ready.
 */
export const DEFERRED_REGISTRATION_PLAYBACK_MAX_MS = 120_000

/**
 * When on-air remaining is unknown, wait this long (not {@link DEFERRED_REGISTRATION_PLAYBACK_MAX_MS}).
 * Boundary notify still flushes earlier when the frame actually ends.
 */
export const DEFERRED_REGISTRATION_PLAYBACK_UNKNOWN_REMAINING_MS = 45_000

/**
 * After head assets are ready, never wait more than this for the current frame to end
 * before force-applying. Natural boundaries (frame advance / hybrid EOF) still win earlier.
 * Keep short so Studio assign feels snappy once the first frame is cached (8s still felt loose
 * on Publicar ↔ Campanha Julho swaps).
 */
export const DEFERRED_REGISTRATION_PLAYBACK_READY_MAX_REMAINING_MS = 2_500

export function onAirFrameRemainingMs(timing: {
  frameElapsedMs: number
  frameDurationMs: number
} | null | undefined): number | null {
  if (timing == null) {
    return null
  }
  const duration = timing.frameDurationMs
  const elapsed = timing.frameElapsedMs
  if (!(duration > 0) || !Number.isFinite(duration) || !Number.isFinite(elapsed)) {
    return null
  }
  return Math.max(0, duration - elapsed)
}

/**
 * True when the notified on-air slot is a video facet. `null` if unknown/empty —
 * callers must keep the ready remaining cap (do not assume a still).
 */
export function onAirQueueFrameExpectsVideo(
  queue: ReadonlyArray<{ facets?: { main?: { type?: string } | null } | null }>,
  notifiedIdx: number | null | undefined
): boolean | null {
  if (queue.length === 0) {
    return null
  }
  if (
    notifiedIdx == null
    || !Number.isFinite(notifiedIdx)
    || notifiedIdx < 0
    || notifiedIdx >= queue.length
  ) {
    return null
  }
  const idx = Math.floor(notifiedIdx)
  const type = queue[idx]?.facets?.main?.type
  if (type == null || type === '') {
    return null
  }
  return type === 'video'
}

/**
 * Delay until forced flush: finish the current frame when remaining is known,
 * never exceed {@link maxMs}. When {@link readyAtBoundary}, also cap at
 * {@link DEFERRED_REGISTRATION_PLAYBACK_READY_MAX_REMAINING_MS}.
 */
export function deferredRegistrationFlushDelayMs(options?: {
  frameRemainingMs?: number | null
  maxMs?: number
  unknownRemainingMs?: number
  /** Head frame ready — snappier assign; do not sit out a long remaining dwell. */
  readyAtBoundary?: boolean
  readyMaxRemainingMs?: number
  /**
   * When head is ready and the on-air slot is a still/widget (not video), flush now.
   * Cutting a still is not a mid-clip tear. `null` / true keep the ready remaining cap.
   */
  onAirExpectsVideo?: boolean | null
}): number {
  if (options?.readyAtBoundary === true && options.onAirExpectsVideo === false) {
    return 0
  }
  const readyCap = options?.readyAtBoundary === true
    ? (options.readyMaxRemainingMs ?? DEFERRED_REGISTRATION_PLAYBACK_READY_MAX_REMAINING_MS)
    : null
  const maxMs = options?.maxMs ?? DEFERRED_REGISTRATION_PLAYBACK_MAX_MS
  const effectiveMax
    = readyCap != null && Number.isFinite(readyCap) && readyCap > 0
      ? Math.min(maxMs, readyCap)
      : maxMs
  const unknownMs = options?.unknownRemainingMs
    ?? (readyCap != null && Number.isFinite(readyCap) && readyCap > 0
      ? readyCap
      : DEFERRED_REGISTRATION_PLAYBACK_UNKNOWN_REMAINING_MS)
  if (!(effectiveMax > 0) || !Number.isFinite(effectiveMax)) {
    return DEFERRED_REGISTRATION_PLAYBACK_MAX_MS
  }
  const remaining = options?.frameRemainingMs
  if (remaining == null || !Number.isFinite(remaining)) {
    return Math.min(unknownMs, effectiveMax)
  }
  if (remaining <= 0) {
    return 0
  }
  return Math.min(Math.ceil(remaining), effectiveMax)
}

/**
 * Soft-defer starvation timer must honor an absolute wall deadline so repeated
 * registration-status polls cannot reset the ceiling forever (#1634 field stuck).
 */
export function deferredRegistrationForceDelayUntilDeadlineMs(options: {
  deadlineAtMs: number
  nowMs?: number
  frameRemainingMs?: number | null
  readyAtBoundary?: boolean
  readyMaxRemainingMs?: number
  onAirExpectsVideo?: boolean | null
}): number {
  const now = options.nowMs ?? Date.now()
  const untilDeadline = Math.max(0, options.deadlineAtMs - now)
  if (untilDeadline <= 0) {
    return 0
  }
  return deferredRegistrationFlushDelayMs({
    frameRemainingMs: options.frameRemainingMs,
    maxMs: untilDeadline,
    readyAtBoundary: options.readyAtBoundary,
    readyMaxRemainingMs: options.readyMaxRemainingMs,
    onAirExpectsVideo: options.onAirExpectsVideo
  })
}
